
import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material';
import { GridLogicOperator, GridOverlay, useGridApiRef } from '@mui/x-data-grid-pro';
import { Ico_Filter_Active_Component, Ico_Filter_Component, Ico_SortAscending, Ico_SortDescending } from '@sagecentral/core-ui';
import { Sort } from 'models/grids';
import { IProjectOverview } from 'models/projects';
import { CentralDataGridPro, GridBox, GridToolbar } from 'components/shared/grids';
import useProjectTasksColumns from './useProjectTasksColumns';
import { useDensityHandler, useGridFilters, useGridMetadata, useGridSorting, usePersistedPageSize, useResizeHandler } from 'hooks/shared';
import { TaskFilter } from 'models/tasks';
import { usePreference } from 'hooks/preferences';
import { PreferenceType } from 'components/preferences';
import { CustomCircularProgress } from 'components/shared/layout';
import { IWorkItemTask } from 'models/work-items/IWorkItemTask';
import { useWorkItemTasks } from 'hooks/work-items';
import { customLocaleText } from 'models/grids/HeaderFilterLocaleText';

interface ProjectTaskGridProps {
    project: IProjectOverview | null;
    all?: boolean;
    focus?: 'late' | 'late-no-risk' | 'on-schedule' | null;
    statusFilter: TaskFilter;
    showUser?: boolean;
    showProgress?: boolean;
    showStatus?: boolean;
    theme?: Theme;
}

/**
 * Renders an overlay when no rows are found in the grid
 * @param status - The current task filter status
 * @returns GridOverlay component with accessibility attributes
 */
const GridNoRowsOverlay = (status: TaskFilter) => {
    if(status === 'all'){
        return (
            <GridOverlay>
                <Box role="status" aria-live="polite">No tasks found</Box>
            </GridOverlay>
        )
    }
    else{
        return (
            <GridOverlay>
                <Box role="status" aria-live="polite">No {status.replace("-", " ")} tasks found</Box>
            </GridOverlay>
        )
    }
};

const ProjectTaskGrid: React.FC<ProjectTaskGridProps> = (props) => {
    const currentPath = window.location.pathname;
    const apiRef = useGridApiRef();
    const { project, statusFilter, focus } = props;
    const all = props.all ?? false;
    const type = all ? PreferenceType.ProjectOverview : PreferenceType.ProjectMyTasks;
    
    const defaultSort: Sort = {
        field: 'endDate',
        dir: 'asc'
    };
    
    const [ preferencesLoaded, settings, setSettings ] = usePreference(type, false);

    const [columnsModel, visibilityModel, handleColumnVisibilityOrOrderChange] = useProjectTasksColumns({
        status: statusFilter,
        showUser: props.showUser,
        settings, 
        setSettings
    });
    
    const [ sort, handleSortChange, sortModel ] = useGridSorting({
        defaultSort: [ defaultSort ],
        settings,
        setSettings
    });

    const [ filter, handleFilterChange, filterModel ] = useGridFilters({
        settings,
        setSettings
    });
    
    const [ pagination, handlePaginationModelChange ] = usePersistedPageSize({
        settings,
        setSettings: setSettings,
    });
    
    const [ density, handleDensityChange ] = useDensityHandler({
        settings,
        setSettings: setSettings,
    });
    
    const localTheme = useTheme();
    const theme = props.theme ?? localTheme;
    
    const gridMetadata = useGridMetadata(filter, sort, settings?.gridSettings, pagination);    
    const [ data, total, loading, error ] = useWorkItemTasks(project?.id ?? 0, statusFilter, all, focus ?? null, gridMetadata, preferencesLoaded, true, true);

    const width = useResizeHandler(() => true, []);

    const TaskNoRowsOverlay = () => GridNoRowsOverlay(statusFilter);
    const getRows: () => IWorkItemTask[] = () => loading ? [] : data;
    const isFilterApplied = () => ((gridMetadata.filterParam?.filters.length || 0) > 0);
    
    return (
        <GridBox getData={getRows} minRowHeight={52} aria-labelledby="project-tasks-heading">
            <Box sx={{ height: '80vh' }}>
                <CentralDataGridPro
                    apiRef={apiRef}
                    rows={getRows()}
                    rowCount={total}
                    columns={columnsModel}
                    columnBufferPx={40 / 8}
                    rowHeight={52}
                    getRowId={(r) => r.id}
                    getRowHeight={() =>'auto'}
                    filterMode="server"
                    filterModel={filterModel}
                    sortingMode="server"
                    sortModel={sortModel}
                    paginationMode='server'
                    pagination={true}
                    pageSizeOptions={[25, 50, 75, 100]}
                    paginationModel={pagination}
                    density={density ?? 'standard'}
                    initialState={{
                        columns: {
                            columnVisibilityModel: visibilityModel,
                        }
                    }} 
                    onSortModelChange={handleSortChange}
                    onFilterModelChange={handleFilterChange}
                    onColumnVisibilityModelChange={() => handleColumnVisibilityOrOrderChange(apiRef)}                    
                    onColumnWidthChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                    onDensityChange={(newDensity) => handleDensityChange(newDensity)}
                    onColumnOrderChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                    onPaginationModelChange={handlePaginationModelChange}
                    disableDensitySelector={true}
                    aria-label="Project tasks grid"
                    aria-describedby="project-tasks-grid-description"
                    slots={{
                        loadingOverlay: () => (<CircularProgress/>),
                        columnMenuSortAscendingIcon: Ico_SortAscending,
                        columnMenuSortDescendingIcon: Ico_SortDescending,
                        columnSortedAscendingIcon: Ico_SortAscending,
                        columnSortedDescendingIcon: Ico_SortDescending,
                        columnUnsortedIcon: undefined,
                        openFilterButtonIcon: isFilterApplied() ? Ico_Filter_Active_Component : Ico_Filter_Component,
                        quickFilterIcon: Ico_Filter_Component,
                        columnFilteredIcon: Ico_Filter_Component,
                        quickFilterClearIcon: Ico_Filter_Component,
                        columnMenuFilterIcon: Ico_Filter_Component,
                        noRowsOverlay: TaskNoRowsOverlay,
                        toolbar: GridToolbar
                    }}
                    headerFilters
                    localeText={customLocaleText}
                    loading={loading || !data}
                    getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? 'row even' : 'row odd'
                    }
                />
            </Box>
            
        </GridBox>
    );
};

export default ProjectTaskGrid;